import type { ContractTypeFilter, SourceMeta } from '@cibli/shared';

import { EmploymentType, Job } from '@cibli/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { browseJobs, getJobs } from '@/services/job';

const DEBOUNCE_DELAY = 500;

interface UseFilteredJobsOptions {
  cvId: string | null;
}

export const useFilteredJobs = (options: UseFilteredJobsOptions) => {
  const { cvId } = options;
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  // Local filter states (with debounce)
  const [localSearch, setLocalSearch] = useState('');

  // Debounced filter states (used for client-side filtering)
  const [searchFilter, setSearchFilter] = useState('');

  // Filter states (sent to API: ATS positions only; empty or 'ALL' = no filter)
  const [contractTypesFilter, setContractTypesFilter] = useState<ContractTypeFilter[]>([]);
  const [employmentTypesFilter, setEmploymentTypesFilter] = useState<EmploymentType[]>([]);

  // Data states
  const [jobs, setJobs] = useState<Job[]>([]);
  const [sourcesMeta, setSourcesMeta] = useState<SourceMeta[]>([]);

  // UI states
  const [isLoading, setIsLoading] = useState(true);

  const fetchJobs = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (cvId) {
        const activeContractFilters = contractTypesFilter.filter(f => f !== 'ALL');
        const response = await getJobs(
          cvId,
          undefined,
          searchFilter.trim() || undefined,
          undefined,
          activeContractFilters.length > 0 ? activeContractFilters : undefined,
          employmentTypesFilter.length > 0 ? employmentTypesFilter : undefined,
        );
        setJobs(response.jobs);
        setSourcesMeta(response.sourcesMeta);
      } else {
        const response = await browseJobs(searchFilter.trim() || undefined);
        setJobs(response.jobs);
        setSourcesMeta(response.sourcesMeta);
      }
    } catch (err) {
      setJobs([]);
      setSourcesMeta([]);
      setError(t('dashboard.jobs.error.loadFailed'));
      console.error('Failed to fetch jobs:', err);
    } finally {
      setIsLoading(false);
    }
  }, [cvId, contractTypesFilter, employmentTypesFilter, searchFilter, t]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchFilter(localSearch);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Fetch jobs when cvId or filters change
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleRetry = useCallback(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleClearFilters = useCallback(() => {
    setLocalSearch('');
    setSearchFilter('');
    setContractTypesFilter([]);
    setEmploymentTypesFilter([]);
  }, []);

  const hasActiveFilters =
    searchFilter.trim() !== '' ||
    contractTypesFilter.length > 0 ||
    employmentTypesFilter.length > 0;

  const hasSourceError = useMemo(() => sourcesMeta.some(s => s.status === 'error'), [sourcesMeta]);

  return {
    // Data
    jobs,

    // Filters (local states for inputs)
    filters: {
      search: localSearch,
      contractTypes: contractTypesFilter,
      employmentTypes: employmentTypesFilter,
    },
    setSearch: setLocalSearch,
    setContractTypes: setContractTypesFilter,
    setEmploymentTypes: setEmploymentTypesFilter,
    clearFilters: handleClearFilters,

    // Pagination (simplified - no pagination)
    pagination: {
      isLoading,
      hasMore: false,
      total: jobs.length,
      hasActiveFilters,
    },

    // Actions
    handleLoadMore: () => {}, // No-op, no pagination
    handleRetry,

    // Error
    error,

    // Source health
    hasSourceError,
    sourcesMeta,
  };
};
